import React, { useState, useRef, useEffect } from 'react';
import { Sun, Moon, Monitor, Palette, Check } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';

const ThemeToggle: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { 
    theme, 
    colorScheme, 
    colorSchemes, 
    setThemeMode, 
    setColorScheme,
    currentColorScheme 
  } = useTheme();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getThemeIcon = () => {
    switch (theme) {
      case 'light':
        return Sun;
      case 'dark':
        return Moon;
      default:
        return Monitor;
    }
  };

  const toggleTheme = () => {
    setThemeMode(theme === 'light' ? 'dark' : 'light');
  };

  const themeOptions = [
    { value: 'light', label: 'Açık Tema', icon: Sun },
    { value: 'dark', label: 'Koyu Tema', icon: Moon },
    { value: 'auto', label: 'Sistem Teması', icon: Monitor }
  ];

  const IconComponent = getThemeIcon();

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-full text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors relative"
        title={`Tema: ${theme} | Renk: ${currentColorScheme.name}`}
      >
        <div className="relative">
          <IconComponent className="w-5 h-5" />
          <div 
            className="absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-white dark:border-gray-800"
            style={{ backgroundColor: currentColorScheme.primary }}
          />
        </div>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
          {/* Header */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <Palette className="w-5 h-5 text-gray-600 dark:text-gray-400 mr-2" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Tema ve Renk Ayarları
              </h3>
            </div>
          </div>

          {/* Theme Selection */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wider">
              Tema Modu
            </h4>
            <div className="space-y-2">
              {themeOptions.map((option) => {
                const OptionIcon = option.icon;
                return (
                  <button
                    key={option.value}
                    onClick={() => {
                      setThemeMode(option.value as any);
                      setIsOpen(false);
                    }}
                    className={`w-full flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                      theme === option.value
                        ? 'bg-primary/10 dark:bg-primary/20 text-primary-dark dark:text-accent'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <OptionIcon className="w-4 h-4 mr-3" />
                    <span className="flex-1 text-left">{option.label}</span>
                    {theme === option.value && (
                      <Check className="w-4 h-4 text-primary dark:text-primary" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Color Scheme Selection */}
          <div className="p-4">
            <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wider">
              Renk Şeması
            </h4>
            <div className="grid grid-cols-4 gap-2">
              {Object.entries(colorSchemes).map(([key, scheme]) => (
                <button
                  key={key}
                  onClick={() => {
                    setColorScheme(key as any);
                  }}
                  className={`relative group p-3 rounded-lg border-2 transition-all ${
                    colorScheme === key
                      ? 'border-gray-400 dark:border-gray-500 bg-gray-50 dark:bg-gray-700'
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                  }`}
                  title={scheme.name}
                >
                  <div className="flex flex-col items-center space-y-2">
                    <div 
                      className="w-6 h-6 rounded-full shadow-sm"
                      style={{ backgroundColor: scheme.primary }}
                    />
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      {scheme.name}
                    </span>
                  </div>
                  
                  {colorScheme === key && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  )}

                  {/* Color Preview */}
                  <div className="absolute bottom-1 left-1 right-1 h-1 rounded-full opacity-60 flex space-x-0.5">
                    <div 
                      className="flex-1 rounded-full"
                      style={{ backgroundColor: scheme.primary }}
                    />
                    <div 
                      className="flex-1 rounded-full"
                      style={{ backgroundColor: scheme.secondary }}
                    />
                    <div 
                      className="flex-1 rounded-full"
                      style={{ backgroundColor: scheme.accent }}
                    />
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Current Selection Info */}
          <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-b-lg">
            <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
              <span>Mevcut Tema:</span>
              <span className="font-medium">
                {themeOptions.find(t => t.value === theme)?.label} • {currentColorScheme.name}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThemeToggle;